import { prepareDataForValidation } from 'formik';
import { cloneDeep, isPlainObject, mergeWith } from 'lodash';
import * as Yup from 'yup';

import { isEmptyValues, nullValuesToEmptyStrings } from '~/Utils';

// getAdditionalDataValidations omits the field types it emits no validation for: an undefined entry in
// Yup.object().shape() makes yup throw when the containing object value is absent.
export type SectionFieldSchemas = Record<string, Yup.AnySchema>;

type SectionValues = Record<string, unknown>;

interface SeedAndValues {
  /** What the section's fragment seeds for the fields currently in scope: '' per field, [] per multiselect. */
  seedValues?: unknown;
  /** What the section actually has stored. */
  sectionValues?: unknown;
}

const asRecord = (value: unknown): SectionValues => (isPlainObject(value) ? (value as SectionValues) : {});

export const buildSectionSchema = (fieldSchemas: SectionFieldSchemas): Yup.AnyObjectSchema =>
  Yup.object().shape({ ...fieldSchemas });

/**
 * A section's stored values laid over its fragment's seed. A field a sub-type change has just brought into
 * scope has no stored key, so it needs its seeded blank back to render and to be judged like a stored one.
 */
export const seedSectionValues = ({ seedValues, sectionValues }: SeedAndValues): SectionValues =>
  mergeWith(
    cloneDeep(asRecord(seedValues)),
    cloneDeep(asRecord(sectionValues)),
    // A stored array replaces the seeded one whole: lodash otherwise merges element by element, so
    // ['x','y'] under ['z'] yields ['z','y'] and a longer seed's tail shows through.
    (_seeded, stored) => (Array.isArray(stored) ? stored : undefined)
  );

/**
 * The save-path inverse of the seed, so the seed renders and validates but never writes: drops, at every
 * depth, each key `priorValues` did not carry that the user left empty. A key `priorValues` DID carry
 * stays even when empty — that is the user clearing an answer, and the per-key PATCH merge needs it.
 */
export const dropUnfilledSeededValues = ({
  values,
  priorValues,
}: {
  values: unknown;
  /** The values the dialog would have opened with before it started seeding them. */
  priorValues?: unknown;
}): SectionValues => {
  const prior = asRecord(priorValues);
  const kept: SectionValues = {};
  Object.entries(asRecord(values)).forEach(([key, value]) => {
    const next = isPlainObject(value) ? dropUnfilledSeededValues({ values: value, priorValues: prior[key] }) : value;
    if (key in prior || !isEmptyValues(next)) {
      kept[key] = next;
    }
  });
  return kept;
};

/**
 * Whether a section's stored values still satisfy its schema. Stored blanks are '' and null, so they are
 * normalised exactly as the dialog normalises them before validating the same schema — otherwise the gate
 * refuses a save the dialog just accepted. `make_ncic_code` is the one optional schema that tells a stored
 * null from '' apart.
 */
export const isSectionSatisfied = ({
  fieldSchemas,
  sectionValues,
  seedValues,
}: SeedAndValues & {
  fieldSchemas: SectionFieldSchemas;
}): boolean =>
  buildSectionSchema(fieldSchemas).isValidSync(
    prepareDataForValidation(
      seedSectionValues({ seedValues, sectionValues: sectionValues && nullValuesToEmptyStrings(sectionValues) })
    )
  );
